"use client";

import type { CmsPhraseFragment } from "@workspace/casino_core/fragments/CmsPhraseFragment";
import CmsPhrase from "@workspace/cms-phrases/src/phrase/CmsPhrase";
import resolvePhrase from "@workspace/cms-phrases/src/phrase/resolvePhrase";
import { SidebarMenuButton, SidebarMenuItem } from "@workspace/frontend-ui";
import LinkComponent from "@workspace/nextjs_utils/src/components/LinkComponent";
import { usePathname } from "next/navigation";
import type { FC } from "react";
import { useEffect, useState } from "react";
import DynamicHugeIcon from "../../../../utils/DynamicHugeIcon";
import { SIDEBAR_TOOLTIP_CLASS } from "../sidebarTooltip";

type Props = {
  brandSlug: string;
  phrases: CmsPhraseFragment[];
  lang: string;
  favoritesCount?: number;
  recentsCount?: number;
  refreshFavouritesCount?: () => Promise<number | undefined>;
};

const quickItems = [
  {
    key: "favorites",
    phraseKey: "sidebar.item.favorites",
    href: "/games/favourite",
    iconName: "StarIcon",
    iconStyle: "solid" as const,
    hideTitle: true,
  },
  {
    key: "recents",
    phraseKey: "sidebar.item.recents",
    href: "/games/recent",
    iconName: "Clock02Icon",
    iconStyle: "stroke" as const,
  },
];

const SidebarQuickAccess: FC<Props> = ({
  brandSlug,
  phrases,
  lang,
  favoritesCount,
  recentsCount,
  refreshFavouritesCount,
}) => {
  const [liveFavouritesCount, setLiveFavouritesCount] =
    useState(favoritesCount);

  useEffect(() => {
    if (!refreshFavouritesCount) return;
    const handleFavouriteToggle = async () => {
      const count = await refreshFavouritesCount();
      if (count !== undefined) setLiveFavouritesCount(count);
    };
    window.addEventListener("favouriteToggle", handleFavouriteToggle);
    return () =>
      window.removeEventListener("favouriteToggle", handleFavouriteToggle);
  }, [refreshFavouritesCount]);

  const pathname = usePathname();
  const langPrefix = lang ? `/${lang}` : "";
  const localPath =
    langPrefix && pathname?.startsWith(langPrefix)
      ? pathname.slice(langPrefix.length) || "/"
      : pathname;
  const shouldPrefixCasino = localPath?.startsWith("/casino");

  const getItemHref = (href: string) =>
    shouldPrefixCasino && !href.startsWith("/casino/")
      ? `/casino${href}`
      : href;

  const counts: Record<string, number | undefined> = {
    favorites: liveFavouritesCount,
    recents: recentsCount,
  };

  return (
    <div className="pb-1">
      {/* Expanded: 2-column grid */}
      <div className="grid grid-cols-[2fr_5fr] gap-2 group-data-[collapsible=icon]:hidden!">
        {quickItems.map((item) => {
          const count = counts[item.key];
          const phrase = phrases.find((p) => p.key === item.phraseKey);
          return (
            <LinkComponent
              key={item.key}
              href={getItemHref(item.href)}
              className="flex items-center justify-between gap-1.5 rounded-md bg-secondary px-2.5 py-2 text-sm text-sidebar-foreground/70 transition-colors hover:bg-white/10 hover:text-sidebar-foreground"
            >
              <div className="flex items-center gap-1.5">
                <DynamicHugeIcon
                  iconName={item.iconName}
                  iconStyle={item.iconStyle}
                  size={20}
                />
                {!item.hideTitle && (
                  <CmsPhrase phrase={phrase} brandSlug={brandSlug} />
                )}
              </div>
              {count !== undefined && (
                <span className="flex items-center justify-center text-xs font-medium text-sidebar-foreground bg-background rounded-full px-1 py-0.5 min-w-5">
                  {count}
                </span>
              )}
            </LinkComponent>
          );
        })}
      </div>

      {/* Collapsed: stacked icon buttons */}
      <div className="hidden flex-col gap-1 group-data-[collapsible=icon]:flex!">
        {quickItems.map((item) => {
          const phrase = phrases.find((p) => p.key === item.phraseKey);
          return (
            <SidebarMenuItem key={item.key}>
              <SidebarMenuButton
                asChild
                tooltip={{
                  children: resolvePhrase(brandSlug, phrase),
                  className: SIDEBAR_TOOLTIP_CLASS,
                }}
                className="hover:text-sidebar-primary"
              >
                <LinkComponent href={getItemHref(item.href)}>
                  <DynamicHugeIcon
                    iconName={item.iconName}
                    iconStyle={item.iconStyle}
                  />
                </LinkComponent>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </div>
    </div>
  );
};

export default SidebarQuickAccess;
